/**
 * Theme Editor Utilities
 * Provides helper functions for making custom components theme editor compatible
 */

// Ensure the theme editor registration system is available
if (!window.registerThemeEditorComponent) {
  console.warn('Theme editor registration system not available. Make sure theme-editor.js is loaded first.');
}

/**
 * Creates a theme editor compatible component initializer
 * @param {string} selector - CSS selector for the component
 * @param {Function} initFunction - Function to initialize the component
 * @param {string} componentName - Name for debugging purposes
 * @returns {Function} Initializer function
 */
window.createThemeEditorComponent = function(selector, initFunction, componentName) {
  return function() {
    const elements = document.querySelectorAll(selector + ':not([data-initialized])');
    elements.forEach(element => {
      element.setAttribute('data-initialized', 'true');
      initFunction(element);
    });
  };
};

/**
 * Registers a component with the theme editor system
 * @param {string} selector - CSS selector for the component
 * @param {Function} initFunction - Function to initialize the component
 * @param {string} componentName - Name for debugging purposes
 */
window.registerComponentWithThemeEditor = function(selector, initFunction, componentName) {
  const initializer = window.createThemeEditorComponent(selector, initFunction, componentName);
  
  // Initialize immediately
  initializer();
  
  // Register for theme editor updates
  if (window.registerThemeEditorComponent) {
    window.registerThemeEditorComponent(initializer, componentName);
  }
};

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
window.debounce = function(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for performance optimization
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
window.throttle = function(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Check if we're in theme editor mode
 * @returns {boolean} True if in theme editor
 */
window.isThemeEditor = function() {
  return window.Shopify && window.Shopify.designMode;
};

/**
 * Wait for element to be available in DOM
 * @param {string} selector - CSS selector
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Element>} Promise that resolves with the element
 */
window.waitForElement = function(selector, timeout = 5000) {
  return new Promise((resolve, reject) => {
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      const element = document.querySelector(selector);
      if (element) {
        obs.disconnect();
        resolve(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Element ${selector} not found within ${timeout}ms`));
    }, timeout);
  });
};
